import {
  TimesheetBreakDurationRuleFragment,
  TimesheetBreakSettingFragment,
  TimesheetPageShiftFragment,
} from 'generated/resources/graphql'

import i18n from 'modules/core/lib/i18n'

import AttendanceShiftUtils from './utils/AttendanceShift'

type AttendanceBreakSetting = TimesheetBreakSettingFragment
type AttendanceBreakDurationRule = TimesheetBreakDurationRuleFragment

export interface ValidationError {
  error: string
  periodLength?: number
  minimalDuration?: number
  shiftId?: number
}

export enum ErrorTypes {
  REST_TIME = 'rest_time',
  BREAK_MINIMUM = 'break_minimum',
}

export const calculateOpenShiftDuration = (clockIn: string, clockOut: string): number => {
  const clockInMinutes = clockIn.split(':').reduce((acc, time) => acc * 60 + parseInt(time), 0)
  const clockOutMinutes = clockOut.split(':').reduce((acc, time) => acc * 60 + parseInt(time), 0)
  // if the clockOut time is before the clockIn time, it means the shift ends on the next day
  if (clockOutMinutes < clockInMinutes) {
    return 1440 - clockInMinutes + clockOutMinutes
  } else {
    return clockOutMinutes - clockInMinutes
  }
}

export const isBreakSetting = (input: unknown): input is AttendanceBreakSetting => {
  return input !== undefined && input !== null && Object.keys(input).includes('id')
}

export const formatBreakTime = (minutes: number): string => {
  const hours = Math.floor(minutes / 60)
  const remainingMinutes = minutes % 60

  const hourText = i18n.tp('common.hour', { count: hours })
  const minuteText = i18n.tp('common.minute', { count: remainingMinutes })

  if (hours > 0 && remainingMinutes > 0) {
    return `${hourText} ${minuteText}`
  }

  if (hours > 0) {
    return hourText
  }

  if (remainingMinutes > 0) {
    return minuteText
  }

  return i18n.t('common.minute.other', { count: 0 })
}

const calculateDurationOfSingleShift = (shift: TimesheetPageShiftFragment) => {
  const clockInTime = AttendanceShiftUtils.clockIn(shift).seconds(0)
  const clockOutTime = AttendanceShiftUtils.clockOut(shift).seconds(0)
  return clockOutTime.diff(clockInTime, 'minutes')
}

const calculateTotalTimeForShifts = (shifts: TimesheetPageShiftFragment[]) => {
  return shifts.reduce((totalDuration, shift) => {
    const shiftDuration = calculateDurationOfSingleShift(shift)
    return totalDuration + shiftDuration
  }, 0)
}

const createShiftDurationDetailsObject = (
  shifts: TimesheetPageShiftFragment[]
): { duration: number; shiftIndex: number; shiftId: number }[] => {
  const durations: { duration: number; shiftIndex: number; shiftId: number }[] = []
  let totalDuration = 0
  let shiftIndex = 0

  shifts.forEach((shift) => {
    const shiftDuration = calculateDurationOfSingleShift(shift)
    totalDuration += shiftDuration
    durations.push({ duration: totalDuration, shiftIndex, shiftId: shift.id })
    shiftIndex++
  })

  return durations
}

const validateBreakDurationRules = (
  relatedShifts: TimesheetPageShiftFragment[] | [],
  breakSetting:
    | Pick<
        AttendanceBreakSetting,
        'restTime' | 'attendanceBreakDurationRules' | 'baseMinimalDuration'
      >
    | undefined,
  currentShift?: TimesheetPageShiftFragment
): ValidationError[] => {
  if (breakSetting === undefined || !breakSetting.restTime) {
    return []
  }

  const breakDurationRules = breakSetting?.attendanceBreakDurationRules
  const workableShifts: TimesheetPageShiftFragment[] = []
  const takenBreaks: TimesheetPageShiftFragment[] = []
  relatedShifts.forEach((shift) => {
    if (shift?.workable) {
      workableShifts.push(shift)
    } else {
      takenBreaks.push(shift)
    }
  })

  const errors: ValidationError[] = []

  const currentShiftDuration = !!currentShift ? calculateDurationOfSingleShift(currentShift) : 0

  const totalBreaks = calculateTotalTimeForShifts(takenBreaks)
  const workableShiftDurations = createShiftDurationDetailsObject(workableShifts)

  let totalWorked = 0
  let currentShiftIndex = -1
  if (relatedShifts.length > 0 && !!currentShift) {
    totalWorked = currentShift.workable
      ? currentShiftDuration + calculateTotalTimeForShifts(workableShifts)
      : 0
    currentShiftIndex = workableShiftDurations.length
  } else if (relatedShifts.length === 0 && !!currentShift) {
    totalWorked = currentShift.workable ? currentShiftDuration : 0
  }

  const validateRestTimeRules = (
    rule: AttendanceBreakDurationRule,
    shiftDetails: { duration: number; shiftIndex: number; shiftId: number }
  ) => {
    const periodLengthInMinutes = Math.round(rule.periodLength * 0.6)
    const totalWorkedUpToShift = shiftDetails.duration

    const existingError = errors.find(
      (error) =>
        error.error === ErrorTypes.REST_TIME &&
        error.periodLength === periodLengthInMinutes &&
        error.minimalDuration === rule.minimalDuration
    )

    if (!existingError && totalWorkedUpToShift >= periodLengthInMinutes) {
      if (totalBreaks < rule.minimalDuration) {
        errors.push({
          error: ErrorTypes.REST_TIME,
          periodLength: periodLengthInMinutes,
          minimalDuration: rule.minimalDuration,
          shiftId: shiftDetails.shiftId,
        })
      }
    }
  }

  const validateBreakTimeRules = (shift: TimesheetPageShiftFragment) => {
    if (!breakSetting || !breakSetting.baseMinimalDuration || !shift.clockOut) {
      return []
    }
    const breakDuration = calculateDurationOfSingleShift(shift)
    if (breakDuration < breakSetting.baseMinimalDuration) {
      errors.push({
        error: ErrorTypes.BREAK_MINIMUM,
        minimalDuration: breakSetting.baseMinimalDuration,
        shiftId: shift.id,
      })
    }
  }

  if (!currentShift) {
    takenBreaks.forEach((breakShift) => {
      validateBreakTimeRules(breakShift)
    })
    workableShiftDurations.forEach((shiftDetails) => {
      breakDurationRules?.forEach((rule) => {
        validateRestTimeRules(rule, shiftDetails)
      })
    })
  } else {
    if (!currentShift.workable) {
      validateBreakTimeRules(currentShift)
    } else {
      const currentShiftDetails = {
        duration: totalWorked,
        shiftIndex: currentShiftIndex,
        shiftId: currentShift.id,
      }
      breakDurationRules?.forEach((rule: AttendanceBreakDurationRule) => {
        validateRestTimeRules(rule, currentShiftDetails)
      })
    }
  }

  return errors
}

export default validateBreakDurationRules
